import { type QueryClient, useQuery } from "@tanstack/react-query";
import { createRootRouteWithContext, Link, Outlet } from "@tanstack/react-router";
import { CompassIcon, TriangleAlertIcon } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Toaster } from "sonner";
import { AppShell } from "@/components/app/app-shell";
import { CommandPaletteProvider } from "@/components/app/command-palette";
import { EmptyState } from "@/components/app/empty-state";
import { GarageNotFound } from "@/components/app/garages";
import { Page } from "@/components/app/page";
import { Button } from "@/components/ui/button";
import { setLanguage } from "@/i18n";
import { CurrentVehicleProvider } from "@/lib/current-vehicle";
import { errorMessage } from "@/lib/errors";
import { garage, rememberGarage } from "@/lib/garage";
import { useTheme } from "@/lib/theme";
import { useTRPC } from "@/lib/trpc";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootLayout,
  notFoundComponent: NotFound,
  errorComponent: RouteError,
});

function RootLayout() {
  const { theme } = useTheme();
  const trpc = useTRPC();
  const { data: garages } = useQuery(trpc.garages.list.queryOptions());
  const exists = !garages || garages.some((g) => g.name === garage);
  useEffect(() => {
    if (garages && exists && garage) rememberGarage(garage);
  }, [garages, exists]);
  // Everyone in a garage sees it in its language, once a garage admin chose one
  // (architecture.md › Garage settings).
  const { data: settings } = useQuery({
    ...trpc.settings.get.queryOptions(),
    enabled: Boolean(garage) && exists,
  });
  const language = settings?.language;
  useEffect(() => {
    if (language) setLanguage(language);
  }, [language]);

  if (!exists) return <GarageNotFound garages={garages ?? []} />;
  return (
    <CurrentVehicleProvider>
      <CommandPaletteProvider>
        <AppShell>
          <Outlet />
        </AppShell>
        <Toaster
          theme={theme}
          position="bottom-right"
          offset={{ bottom: 24 }}
          mobileOffset={{ bottom: 84 }}
          toastOptions={{
            classNames: {
              toast: "!rounded-xl !border-border !bg-surface-raised !text-foreground !shadow-lg",
            },
          }}
        />
      </CommandPaletteProvider>
    </CurrentVehicleProvider>
  );
}

function NotFound() {
  const { t } = useTranslation();
  return (
    <Page>
      <EmptyState
        icon={CompassIcon}
        title={t("errors.NOT_FOUND")}
        action={
          <Button asChild variant="outline">
            <Link to="/">{t("nav.overview")}</Link>
          </Button>
        }
        className="mt-10"
      />
    </Page>
  );
}

function RouteError({ error, reset }: { error: unknown; reset: () => void }) {
  const { t } = useTranslation();
  return (
    <Page>
      <EmptyState
        icon={TriangleAlertIcon}
        title={t("errors.pageTitle")}
        body={errorMessage(error, t)}
        action={
          <>
            <Button variant="outline" onClick={reset}>
              {t("common.retry")}
            </Button>
            <Button asChild variant="ghost">
              <Link to="/">{t("nav.overview")}</Link>
            </Button>
          </>
        }
        className="mt-10"
      />
    </Page>
  );
}
